import React, { useState } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { createCheckoutSession } from '../../../services/stripeService';
import './Pricing.css';

const Pricing = () => {
    const MySwal = withReactContent(Swal);
    const [processing, setProcessing] = useState(false);

    const pricingPlans = [
        {
            id: 'starter',
            typeAbonnement: 1,
            icon: '🌱',
            name: 'Starter',
            description: 'Idéal pour démarrer la digitalisation de votre restaurant',
            price: '49,90€',
            priceValue: 4990, // En centimes pour Stripe
            currency: 'eur',
            period: '/mois',
            priceNote: 'Prix HT',
            isPopular: false,
            borderColor: '#E5E7EB',
            features: [
                '1 restaurant / point de vente',
                '1 gérant',
                'Utilisateurs illimités',
                '20 tables QR',
                'Tableau de bord complet',
                'Indicateurs essentiels',
                'Formation à distance'
            ]
        },
        {
            id: 'business',
            typeAbonnement: 2,
            icon: '⚙️',
            name: 'Business',
            description: 'Pour les restaurants multi-sites avec des besoins avancés',
            price: '79,90€',
            priceValue: 7990, // En centimes pour Stripe
            currency: 'eur',
            period: '/mois',
            priceNote: 'Prix HT',
            isPopular: true,
            borderColor: '#FF6B2C',
            features: [
                'Jusqu\'à 3 restaurants',
                '3 gérants',
                '40 tables QR',
                'Indicateurs avancés',
                'Gestion des stocks avancée',
                'Formation sur site',
                'Assistance prioritaire'
            ]
        },
        {
            id: 'enterprise',
            typeAbonnement: 3,
            icon: '🏢',
            name: 'Enterprise',
            description: 'Solution complète pour les grands groupes de restauration',
            price: 'Sur mesure',
            priceValue: 10990, // En centimes pour Stripe
            currency: 'eur',
            period: '',
            priceNote: '',
            isPopular: false,
            borderColor: '#8B5CF6',
            features: [
                'Restaurants illimités',
                'Gérants illimités',
                'QR illimitées',
                'Tableau de bord sur mesure',
                'Intégrations personnalisées',
                'Gestionnaire de compte dédié',
                'Assistance 24/7'
            ]
        }
    ];

    // Fonction pour gérer le paiement Stripe
    const handleSubscription = async (plan) => {
        if (processing) return;

        setProcessing(true);

        try {
            const user = JSON.parse(localStorage.getItem('user'));
            if (!user) {
                MySwal.fire({
                    icon: 'warning',
                    // title: 'Connexion requise',
                    text: 'Veuillez vous connecter pour souscrire à un abonnement.',
                    confirmButtonColor: '#FF6B2C',
                });
                setProcessing(false);
                return;
            }

            // Confirmation avant paiement
            const confirmation = await MySwal.fire({
                // title: '💳 Souscrire à l\'abonnement',
                html: `
                    <div style="text-align: center; padding: 20px;">
                        <div style="font-size: 48px; margin-bottom: 16px;">${plan.icon}</div>
                        <h3 style="color: #333; margin-bottom: 8px;">${plan.name}</h3>
                        <p style="color: #666; font-size: 14px; margin-bottom: 20px;">${plan.description}</p>
                        <div style="background: #f8f9fa; padding: 20px; border-radius: 12px; margin-bottom: 20px;">
                            <div style="display: flex; justify-content: center; align-items: baseline; gap: 4px;">
                                <span style="color: #FF6B2C; font-size: 32px; font-weight: bold;">${plan.price}</span>
                                <span style="color: #666; font-size: 14px;">${plan.period}</span>
                            </div>
                            <p style="color: #888; font-size: 12px; margin-top: 8px;">${plan.priceNote}</p>
                        </div>
                        <p style="color: #666; font-size: 14px;">
                            🔐 Paiement sécurisé par Stripe
                        </p>
                    </div>
                `,
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#28a745',
                cancelButtonColor: '#6c757d',
                confirmButtonText: '🔐 Procéder au paiement',
                cancelButtonText: '❌ Annuler',
                width: '450px',
            });

            if (!confirmation.isConfirmed) {
                setProcessing(false);
                return;
            }

            // Afficher le loader
            MySwal.fire({
                // title: '🔐 Connexion à Stripe...',
                html: `
                    <div style="text-align: center;">
                        <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem; margin: 20px 0;">
                            <span class="sr-only">Chargement...</span>
                        </div>
                        <p style="color: #666; margin-top: 15px;">Création de la session de paiement...</p>
                    </div>
                `,
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    MySwal.showLoading();
                }
            });

            // Générer une référence unique pour l'abonnement
            const reference = `abo-${plan.id}-${Date.now()}`;

            // Stocker les infos du plan dans localStorage pour les récupérer après le retour Stripe
            localStorage.setItem('pending_subscription', JSON.stringify({
                plan: plan,
                reference: reference,
                timestamp: new Date().toISOString()
            }));

            // Créer la session Stripe avec la clé par défaut de l'application
            const sessionData = await createCheckoutSession({
                reference: reference,
                amount: plan.priceValue,
                currency: plan.currency,
                notes: `Abonnement ${plan.name} - ${plan.price}${plan.period}`,
                success_url: `${window.location.origin}/subscription-success?session_id={CHECKOUT_SESSION_ID}`,
                cancel_url: `${window.location.origin}/pricing`,
                customer_email: user.email || '',
                useDefaultKey: true // ⚠️ Force l'utilisation de la clé Stripe de l'application (pas celle du restaurant)
            });

            console.log('✅ Session Stripe créée:', sessionData);

            MySwal.close();

            // Afficher succès avant redirection
            MySwal.fire({
                icon: 'success',
                // title: 'Session créée !',
                html: `<p style="color: #666;">Redirection vers Stripe Checkout...</p>`,
                toast: true,
                position: 'bottom-end',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
                customClass: { popup: "pixel-toast" }
            });

            // Redirection vers Stripe
            setTimeout(() => {
                window.location.href = sessionData.url;
            }, 2000);

        } catch (error) {
            console.error('❌ Erreur paiement:', error);

            MySwal.fire({
                icon: 'error',
                // title: 'Erreur de paiement',
                text: error.message || 'Impossible de créer la session de paiement. Veuillez réessayer.',
                confirmButtonColor: '#FF6B2C',
            });
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="pricing-component">
            <div className="pricing-container">
                <div className="pricing-header">
                    <h1 className="pricing-title">Tarification</h1>
                    <p className="pricing-subtitle">Des solutions adaptées à chaque étape de votre croissance</p>
                </div>

                <div className="pricing-cards">
                    {pricingPlans.map((plan) => (
                        <div
                            key={plan.id}
                            className={`pricing-card ${plan.isPopular ? 'popular' : ''} ${processing ? 'disabled' : ''}`}
                            style={{ borderColor: plan.borderColor, cursor: processing ? 'not-allowed' : 'pointer' }}

                        >
                            {plan.isPopular && (
                                <div className="popular-badge">Le plus populaire</div>
                            )}

                            <div className="card-header">
                                <div className="icon">{plan.icon}</div>
                                <h3 className="plan-name">{plan.name}</h3>
                            </div>

                            <p className="plan-description">{plan.description}</p>

                            <div className="pricing-info">
                                <span className="price">{plan.price}</span>
                                <span className="period">{plan.period}</span>
                            </div>
                            <p className="mb-0 price-note">{plan.priceNote}</p>

                            {/* Liste des fonctionnalités  */}
                            <ul className="plan-features">
                                {plan.features.map((feature, index) => (
                                    <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '16px' }}>
                                        <span style={{ color: '#FF6B2C' }}>✔</span>
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            {plan.id !== 'enterprise' && (
                                <button
                                    className=""
                                    onClick={() => handleSubscription(plan)}
                                    style={{
                                        marginTop: '16px',
                                        padding: '12px 24px',
                                        backgroundColor: plan.isPopular ? '#FF6B2C' : '#f5f5f5',
                                        color: plan.isPopular ? 'white' : '#333',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: processing ? 'not-allowed' : 'pointer',
                                        width: '100%',
                                        transition: 'all 0.2s ease'
                                    }}
                                    disabled={processing}
                                >
                                    {processing ? 'Traitement...' : 'Souscrire'}
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                <div className="pricing-footer">
                    <a
                        href="https://qrnyama.com/tarifs.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-learn-more"
                        style={{ textDecoration: 'none', display: 'inline-block' }}
                    >
                        En savoir plus
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Pricing;
